import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, Camera, X, Zap, ScanLine, Trophy, RefreshCw, Lock, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { PredictionPill } from "@/components/StatusBadge";

const ACCEPT = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const STEPS = ["ANALYZING FIXTURE…", "IDENTIFYING MATCHES…", "GENERATING PREDICTIONS…"];

function compress(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const max = 1600;
      let { width, height } = img;
      if (width > max || height > max) {
        const r = Math.min(max / width, max / height);
        width = Math.round(width * r); height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Predictor() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [sub, setSub] = useState(null);
  const [checking, setChecking] = useState(true);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [base64, setBase64] = useState(null);
  const [phase, setPhase] = useState(0); // 0 idle, 1 analyzing
  const [stepIdx, setStepIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);

  const loadSub = () => api.get("/subscription").then(({ data }) => setSub(data)).finally(() => setChecking(false));
  useEffect(() => { loadSub(); }, []);

  useEffect(() => {
    if (phase !== 1) return;
    setStepIdx(0);
    const t = setInterval(() => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)), 650);
    return () => clearInterval(t);
  }, [phase]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPT.includes(file.type)) { toast.error("Unsupported file type. Use JPG, JPEG, PNG or WEBP."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image is too large. Max 10MB."); return; }
    try {
      const data = await compress(file);
      setPreview(data); setBase64(data); setFileName(file.name); setResult(null);
    } catch { toast.error("Could not read this image. Please try another."); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); };

  const analyze = async () => {
    if (!base64) return;
    setPhase(1);
    try {
      const { data } = await api.post("/predict", { image_base64: base64 });
      setResult(data); setSub(data.subscription);
      toast.success("Predictions ready!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "SUBSCRIPTION_REQUIRED") { toast.error("Subscription required."); loadSub(); }
      else if (detail === "SUBSCRIPTION_EXPIRED") { toast.error("Subscription expired."); loadSub(); }
      else toast.error(detail || "Analysis failed. Please try again.");
    } finally {
      setPhase(0);
    }
  };

  const reset = () => { setPreview(null); setBase64(null); setFileName(""); setResult(null); };

  if (checking) return <div className="h-40 grid place-items-center"><div className="h-9 w-9 rounded-full border-2 border-emerald-500/30 border-t-emerald-400 animate-spin" /></div>;

  const active = sub?.has_subscription && sub?.status === "ACTIVE";

  if (!active) {
    const payStatus = sub?.latest_payment_status;
    const expired = sub?.has_subscription;
    let variant;
    if (expired) variant = { title: "SUBSCRIPTION EXPIRED", desc: "Your subscription has expired. Renew a plan and get it approved to keep predicting.", cta: "VIEW PLANS", action: () => navigate("/plans") };
    else if (payStatus === "PENDING") variant = { title: "PAYMENT UNDER REVIEW", desc: "Your payment has been submitted and is awaiting admin approval. The predictor unlocks as soon as it's approved.", cta: "VIEW PAYMENT STATUS", action: () => navigate("/payment-history"), amber: true };
    else if (payStatus === "REJECTED") variant = { title: "PAYMENT REJECTED", desc: "Your payment could not be approved. Please review your payment details and submit a new payment proof.", cta: "SUBMIT NEW PAYMENT", action: () => navigate("/plans"), red: true };
    else variant = { title: "PREDICTOR LOCKED", desc: "Subscribe to a plan and wait for payment approval to start using VirtualWin.", cta: "VIEW PLANS", action: () => navigate("/plans") };

    const tone = variant.red ? { bg: "bg-red-500/15", color: "text-red-400" } : { bg: "bg-amber-500/15", color: "text-amber-400" };
    return (
      <div className="max-w-xl mx-auto text-center py-12" data-testid="predictor-locked">
        <div className={`h-16 w-16 mx-auto grid place-items-center rounded-2xl ${tone.bg} ${tone.color} mb-5`}><Lock size={30} /></div>
        <h1 className="font-display text-2xl font-black text-white" data-testid="predictor-locked-title">{variant.title}</h1>
        <p className="mt-3 text-slate-400">{variant.desc}</p>
        <button onClick={variant.action} className="vw-btn-primary mt-7 inline-flex items-center gap-2" data-testid="predictor-locked-cta">{variant.cta} <ArrowRight size={18} /></button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="predictor-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="vw-label">VirtualWin Predictor</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mt-1">VIRTUALWIN PREDICTOR</h1>
        </div>
        <CreditBadge sub={sub} />
      </div>

      {/* ANALYZING */}
      {phase === 1 && (
        <div className="vw-glow-card p-10 text-center" data-testid="predictor-analyzing">
          <div className="relative h-48 rounded-xl overflow-hidden border border-emerald-500/30 mb-6">
            {preview && <img src={preview} alt="" className="w-full h-full object-cover opacity-50" />}
            <div className="vw-scanline" />
          </div>
          <div className="flex items-center justify-center gap-3">
            <div className="h-6 w-6 rounded-full border-2 border-emerald-500/30 border-t-emerald-400 animate-spin" />
            <span className="font-display font-bold text-white text-lg">{STEPS[stepIdx]}</span>
          </div>
        </div>
      )}

      {/* RESULT */}
      {phase === 0 && result && (
        <div data-testid="predictor-result">
          <div className="space-y-4">
            {result.fixtures.map((f, i) => (
              <div key={i} className="vw-glow-card p-6 vw-fade-up" style={{ animationDelay: `${i * 80}ms` }} data-testid={`result-card-${i}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="font-display text-lg sm:text-xl font-bold text-white">
                    {f.home_team} <span className="text-slate-500 text-base font-normal">vs</span> {f.away_team}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between">
                  <span className="vw-label">Prediction</span>
                  <PredictionPill prediction={f.prediction} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-center font-display text-xl font-bold text-emerald-400 mt-7">Good luck 🍀</p>
          {sub?.status === "ACTIVE" && (
            <button onClick={reset} className="vw-btn-primary mt-6 w-full inline-flex items-center justify-center gap-2" data-testid="analyze-another">
              <RefreshCw size={18} /> ANALYZE ANOTHER FIXTURE
            </button>
          )}
        </div>
      )}

      {/* UPLOAD */}
      {phase === 0 && !result && (
        <div className="vw-card p-6 sm:p-8" data-testid="predictor-upload">
          {!preview ? (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 sm:p-14 text-center transition-all ${dragging ? "border-emerald-400 bg-emerald-500/5" : "border-slate-700 hover:border-emerald-500/50"}`}
              data-testid="upload-dropzone">
              <div className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-emerald-500/10 text-emerald-400 mb-5"><UploadCloud size={32} /></div>
              <h3 className="font-display text-xl font-bold text-white">UPLOAD YOUR VIRTUAL FIXTURE</h3>
              <p className="mt-2 text-sm text-slate-400 max-w-sm mx-auto">Take a screenshot of your virtual football fixtures and upload it here to get started.</p>
              <span className="mt-6 inline-flex items-center gap-2 vw-btn-primary !py-2.5"><Camera size={18} /> 📷 UPLOAD SCREENSHOT</span>
              <p className="mt-4 text-xs text-slate-500 font-mono">JPG · JPEG · PNG · WEBP</p>
              <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="upload-input" />
            </div>
          ) : (
            <div data-testid="predictor-preview">
              <div className="relative rounded-xl overflow-hidden border border-slate-800">
                <img src={preview} alt="preview" className="w-full max-h-[380px] object-contain bg-black/40" />
                <button onClick={reset} className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-black/70 text-white" data-testid="preview-remove"><X size={18} /></button>
              </div>
              <p className="mt-3 text-sm text-slate-400 truncate"><span className="text-slate-500">Selected:</span> {fileName}</p>
              <button onClick={analyze} className="vw-btn-primary mt-5 w-full inline-flex items-center justify-center gap-2" data-testid="analyze-now">
                <Zap size={18} /> ANALYZE NOW
              </button>
            </div>
          )}

          <div className="mt-8 grid grid-cols-3 gap-3 text-center">
            {[{ i: UploadCloud, t: "Upload" }, { i: ScanLine, t: "Analyze" }, { i: Trophy, t: "Predict" }].map((s, idx) => (
              <div key={idx} className="rounded-xl border border-slate-800 p-3">
                <s.i size={18} className="mx-auto text-emerald-400 mb-1.5" />
                <span className="text-xs text-slate-400">{s.t}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const CreditBadge = ({ sub }) => {
  const isTime = sub?.type === "time_pass";
  return (
    <div className="vw-card px-4 py-2.5 text-right" data-testid="predictor-credits">
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{isTime ? "Access" : "Remaining Analyses"}</div>
      <div className="font-display font-bold text-emerald-400">{isTime ? "UNLIMITED" : `${sub?.credits_remaining} of ${sub?.credits_total}`}</div>
    </div>
  );
};
